import {
  AsyncActionButton,
  BackButton,
  CustomError,
  FilteredCreditApplication,
  Loader,
  useMerchantById,
} from '@applyonline/shared'
import { EApplicantType, EDocumentStatus, EDocumentType } from '@if/core/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import UploadFileButton from '../../../../components/UploadFileButton'
import {
  hasApplicantCompletedFlinks,
  hasApplicantStartedFlinksAnalysis,
} from '../../../../data/credit-application-selectors'

interface Props {
  creditApp: FilteredCreditApplication
  applicantType: EApplicantType
}

const ApplicantVoidChequePage = ({ creditApp, applicantType }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [merchant, isLoadingMerchant] = useMerchantById(creditApp.merchantId, creditApp.financingProgramId)

  const hasApplicantFinishedFlinks = hasApplicantCompletedFlinks(creditApp, applicantType)
  const hasApplicantStartedFlinks = hasApplicantStartedFlinksAnalysis(creditApp, applicantType)

  const openFlinks = () => {
    navigate(`/creditapplication/${creditApp.id}/flinks/${applicantType}`)
  }

  const voidChequeDoc = creditApp.documents.find(
    (doc) => doc.typeId === EDocumentType.VoidCheque && doc.applicantType === applicantType,
  )

  const hasSubmittedVoidCheque = creditApp.documents.some(
    (doc) =>
      doc.typeId === EDocumentType.VoidCheque &&
      doc.applicantType === applicantType &&
      [EDocumentStatus.Approved, EDocumentStatus.AwaitingApproval, EDocumentStatus.AwaitingScan].includes(doc.status),
  )

  const [error, setError] = useState<CustomError>()

  if (hasSubmittedVoidCheque)
    return (
      <main className="general-message">
        <div className="paragraph full-width-center-container" style={{ marginBottom: '15px' }}>
          <h4 className="subtitle">{t('voidCheque.title')}</h4>
          <h3>{t('common.voidChequeReceived')}</h3>
        </div>
        <div className="btn-group centered-one-btn-group full-width-mobile">
          <BackButton />
        </div>
      </main>
    )

  if (isLoadingMerchant || !merchant)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  return (
    <main className="general-message">
      <h4 className="subtitle">{t('voidCheque.title')}</h4>
      <div className="applicationInfo-card">
        <div className="paragraph">
          <ol id="flinks-options">
            <li>
              <div>
                {t('voidCheque.description')} <strong>{t('incomeTile.flinksSafe')}</strong>
              </div>
            </li>
            <br />
            <li>
              <div>
                {t('incomeTile.manualOption')}
                <ul>
                  <li>{t('incomeTile.voidCheque')}</li>
                </ul>
              </div>
            </li>
          </ol>
        </div>
        <div className="btn-group space-between" style={{ alignItems: 'flex-end' }}>
          <AsyncActionButton
            primary
            onClick={openFlinks}
            isPending={false}
            disabled={hasApplicantFinishedFlinks || hasApplicantStartedFlinks}
          >
            {t('incomeTile.useFlinks')}
          </AsyncActionButton>

          <div className="flex-col">
            {error && (
              <span
                style={{
                  color: 'red',
                  marginBlockEnd: '1rem',
                  display: 'block',
                  textAlign: 'center',
                }}
              >
                {error.message}
              </span>
            )}
            <UploadFileButton
              text={t('voidCheque.buttonManual')}
              id="upload-void-cheque"
              creditApplicationId={creditApp.id}
              typeId={EDocumentType.VoidCheque}
              subKey={voidChequeDoc?.subKey ?? null}
              applicantType={applicantType}
              disabled={voidChequeDoc?.status === EDocumentStatus.Approved}
              setError={setError}
            />
          </div>
        </div>
      </div>
      <BackButton />
    </main>
  )
}

export default ApplicantVoidChequePage
